/**
 * Customer evidence trail — public schema.
 *
 * Mirrors the backend `EvidenceRegistry` contract documented in
 * ideas/plan_evidence_trail.md. Payloads enter the UI from the assistant
 * message metadata; they are persisted by the registry and never authored
 * client-side. Parse with `EvidenceEventSchema` at the API boundary.
 */

import { z } from "zod/v3";
import { UIComponent } from "./uiComponent";

export const EvidenceKind = {
	PDF_FACT: "pdf_fact",
	WEB_SOURCE: "web_source",
	EXCEL_CHANGE: "excel_change",
	CALCULATION: "calculation",
	COMMS_FACT: "comms_fact",
	RECORD_FACT: "record_fact",
} as const;
export type EvidenceKind = (typeof EvidenceKind)[keyof typeof EvidenceKind];

export const PdfSourceRefSchema = z.object({
	kind: z.literal("pdf"),
	file_id: z.number(),
	filename: z.string(),
	page: z.number().int().nullable().optional(),
	section: z.string().nullable().optional(),
	excerpt: z.string().nullable().optional(),
});

export const WebSourceRefSchema = z.object({
	kind: z.literal("web"),
	url: z.string(),
	title: z.string().nullable().optional(),
	publisher: z.string().nullable().optional(),
	retrieved_at: z.string(),
	snippet: z.string().nullable().optional(),
});

export const ExcelSourceRefSchema = z.object({
	kind: z.literal("excel"),
	filename: z.string(),
	sheet: z.string(),
	// Source workbook (FileAttachment PK).
	file_id: z.number().nullable().optional(),
	// Modified-workbook artifact (block id from manipulate_workbook).
	// When present, this is the workbook that contains the cited change;
	// the resolver opens it via the chat-scoped workbook-blocks endpoint.
	artifact_id: z.string().nullable().optional(),
	range: z.string().nullable().optional(),
	cell: z.string().nullable().optional(),
	formula: z.string().nullable().optional(),
	before: z.unknown().optional(),
	after: z.unknown().optional(),
});

/**
 * A single field of a record-shaped source row — a DealCloud CRM row, a
 * domain-model row, a loan-tape row. `record_label` and `as_of` are display
 * only (the backend excludes them from the ref's identity), and both may be
 * absent on a ref whose producer knew only the locator.
 */
export const RecordSourceRefSchema = z.object({
	kind: z.literal("record"),
	source_system: z.string(),
	object_type: z.string(),
	record_id: z.string(),
	field: z.string(),
	snapshot_sha256: z.string(),
	version: z.string().nullable().optional(),
	record_label: z.string().nullable().optional(),
	as_of: z.string().nullable().optional(),
});

/**
 * One message inside an email thread or Slack channel.
 *
 * `thread_id` is an ingestion slug and is never rendered when `thread_label`
 * (the email subject / Slack channel name) is present. `file_id` is the
 * markdown projection document the message was rendered into — the target of
 * the chip's click-through.
 */
export const CommsSourceRefSchema = z.object({
	kind: z.literal("comms"),
	source_system: z.enum(["email", "slack"]),
	thread_id: z.string(),
	message_id: z.string(),
	sender: z.string(),
	sent_at: z.string(),
	excerpt: z.string(),
	excerpt_hash: z.string(),
	thread_label: z.string().nullable().optional(),
	message_ordinal: z.number().int().nullable().optional(),
	file_id: z.number().nullable().optional(),
});

export const ComputedInputSchema = z.object({
	name: z.string(),
	value: z.unknown().optional(),
	upstream_event_id: z.string(),
});

export const ComputedSourceRefSchema = z.object({
	kind: z.literal("computed"),
	label: z.string(),
	formula: z.string(),
	inputs: z.array(ComputedInputSchema).default([]),
});

export const SourceRefSchema = z.discriminatedUnion("kind", [
	PdfSourceRefSchema,
	WebSourceRefSchema,
	ExcelSourceRefSchema,
	RecordSourceRefSchema,
	CommsSourceRefSchema,
	ComputedSourceRefSchema,
]);

export const OperationSchema = z.object({
	type: z.enum([
		"extraction",
		"search",
		"formula",
		"calculation",
		"workbook_edit",
	]),
	details: z.record(z.unknown()).default({}),
});

export const DebugRefsSchema = z.object({
	langfuse_span_id: z.string().nullable().optional(),
	tool_call_id: z.string().nullable().optional(),
	raw_trace_id: z.string().nullable().optional(),
});

export const EvidenceEventSchema = z.object({
	event_id: z.string(),
	sequence: z.number().int(),
	kind: z.enum([
		EvidenceKind.PDF_FACT,
		EvidenceKind.WEB_SOURCE,
		EvidenceKind.EXCEL_CHANGE,
		EvidenceKind.CALCULATION,
		EvidenceKind.COMMS_FACT,
		EvidenceKind.RECORD_FACT,
	]),
	title: z.string(),
	summary: z.string(),
	source_refs: z.array(SourceRefSchema),
	derived_from: z.array(z.string()).default([]),
	operation: OperationSchema.nullable().optional(),
	debug_refs: DebugRefsSchema.nullable().optional(),
	// Backend-driven dispatch tag. Older payloads (pre-migration) may
	// omit it; the EvidencePanel falls back to ``EVIDENCE_KIND_TO_UI_COMPONENT[kind]``.
	ui_component_type: z
		.enum([
			UIComponent.EVIDENCE_PDF_FACT,
			UIComponent.EVIDENCE_WEB_SOURCE,
			UIComponent.EVIDENCE_EXCEL_CHANGE,
			UIComponent.EVIDENCE_CALCULATION,
			UIComponent.EVIDENCE_COMMS_MESSAGE,
			UIComponent.EVIDENCE_RECORD_FIELD,
		])
		.nullable()
		.optional(),
});

export type PdfSourceRef = z.infer<typeof PdfSourceRefSchema>;
export type WebSourceRef = z.infer<typeof WebSourceRefSchema>;
export type ExcelSourceRef = z.infer<typeof ExcelSourceRefSchema>;
export type RecordSourceRef = z.infer<typeof RecordSourceRefSchema>;
export type CommsSourceRef = z.infer<typeof CommsSourceRefSchema>;
export type ComputedInput = z.infer<typeof ComputedInputSchema>;
export type ComputedSourceRef = z.infer<typeof ComputedSourceRefSchema>;
export type SourceRef = z.infer<typeof SourceRefSchema>;
export type Operation = z.infer<typeof OperationSchema>;
export type DebugRefs = z.infer<typeof DebugRefsSchema>;
export type EvidenceEvent = z.infer<typeof EvidenceEventSchema>;

/** Sort by registry-assigned `sequence` — never by timestamp. */
export const sortBySequence = (events: EvidenceEvent[]): EvidenceEvent[] =>
	[...events].sort((a, b) => a.sequence - b.sequence);

/** Resolve `derived_from` ids to events; skip ids absent from the turn. */
export const upstreamEvents = (
	event: EvidenceEvent,
	all: EvidenceEvent[],
): EvidenceEvent[] => {
	const byId = new Map(all.map((e) => [e.event_id, e]));
	return event.derived_from
		.map((id) => byId.get(id))
		.filter((e): e is EvidenceEvent => e !== undefined);
};
